import { FormData } from "@erinfo/react-utils/src/helpers/form/formDataFormatters"
import { useEffect, useState } from "react"

export interface FieldCounts {
  [type: string]: number
}

export const useDynamicFields = (
  names: string[],
  initializer: (user) => FormData,
  user,
  open: boolean,
  path?: string, // path under user object where collections are mounted
  formFieldMap?: { [key: string]: string[] },
) => {
  const initialFieldCounts: FieldCounts = names.reduce((agg, curr) => {
    agg[curr] =
      (path ? user?.[path]?.[curr]?.length : user?.[curr]?.length) || 1
    return agg
  }, {})

  const [initialValues, setInitialValues] = useState<FormData>()
  const [fieldCounts, setFieldCounts] =
    useState<FieldCounts>(initialFieldCounts)

  useEffect(() => {
    if (user && open) {
      setFieldCounts(initialFieldCounts)
      setInitialValues(initializer(user))
    }
  }, [open, user])

  const addField = (values, setValues) => (type: string, nameField: string) => {
    const idx = fieldCounts[type]
    const fieldNames = formFieldMap?.[type] ?? []

    const noneField = Object.entries(values).reduce(
      (agg, [key, val]) =>
        key.startsWith(`${nameField}_`) && val === `None` ? key : agg,
      ``,
    )
    if (noneField) {
      setValues({
        ...values,
        [noneField]: ``,
      })
    } else {
      setValues({
        ...values,
        ...fieldNames.reduce((agg, curr) => {
          agg[`${curr}_${idx}`] = ``
          return agg
        }, {}),
      })
      setFieldCounts((p) => ({ ...p, [type]: p[type] + 1 }))
    }
  }

  const removeField = (values, setValues) => (type: string, idx: number) => {
    setValues(
      Object.entries(values).reduce((agg, [key, value]) => {
        const [field, fieldIdx] = key.split(`_`)
        const fieldIdxNo = Number(fieldIdx)
        if (!formFieldMap || formFieldMap[type].includes(field)) {
          if (fieldIdxNo < idx) {
            agg[key] = value
          } else if (fieldIdxNo > idx) {
            agg[`${field}_${fieldIdxNo - 1}`] = value
          } else if (fieldIdxNo === idx && fieldCounts[type] === 1) {
            agg[key] = ``
          }
        } else {
          agg[key] = value
        }
        return agg
      }, {}),
    )
    if (fieldCounts[type] > 1) {
      setFieldCounts((p) => ({ ...p, [type]: Math.max(p[type] - 1, 1) }))
    }
  }

  const handleNoneSelection = (values, setValues) => (type: string) => () => {
    const fieldNames = formFieldMap?.[type] ?? []
    const noneForm = fieldNames.reduce((agg, curr, idx) => {
      if (idx === 0) {
        agg[`${curr}_0`] = `None`
      } else {
        agg[`${curr}_0`] = ``
      }
      return agg
    }, {})

    const rest = Object.entries(values).reduce(
      (agg, [key, value]) => {
        const [field] = key.split(`_`)
        if (formFieldMap && !formFieldMap[type].includes(field)) {
          agg[key] = value
        }
        return agg
      },
      { ...noneForm },
    )

    setValues({ ...noneForm, ...rest })
    setFieldCounts((p) => ({ ...p, [type]: 1 }))
  }

  const setFieldValueOrRemove =
    (setFieldValue, removeField, handleNone) =>
    (name: string, value: string) => {
      if (value === "None") {
        return handleNone()
      }
      if (value === undefined || value === null || value === ``) {
        return removeField() // output of above curry function two layers down
      }
      setFieldValue(name, value)
    }

  return {
    initialValues,
    fieldCounts,
    addField,
    removeField,
    setFieldValueOrRemove,
    handleNoneSelection,
  }
}
